import React, { useRef, useState } from 'react';

import { IconScout, type IconScoutName } from '@/components/ui/images/IconScout';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';
import {
  DT_RESOLUTION_EXPIRY_WARNING_DAYS,
  getDaysUntil,
} from '@/utils/dtResolutionValidity';


type ResolutionStatusVariant = 'expired' | 'expiringSoon' | 'valid' | 'none';

interface ShiftResolutionStatusIndicatorProps {
  isExceptionalWorkingDay: boolean;
  dtResolutionExpired: boolean;
  dtResolutionExpiryDate: string | null;
}

interface VariantConfig {
  icon: IconScoutName;
  color: string;
  titleKey: string;
  bodyKey: string;
}

const VARIANT_CONFIG: Record<ResolutionStatusVariant, VariantConfig> = {
  expired: {
    icon: 'timesCircle',
    color: colors.feedback.error300,
    titleKey: 'shift.resolutionStatus.expired.title',
    bodyKey: 'shift.resolutionStatus.expired.body',
  },
  expiringSoon: {
    icon: 'alert',
    color: colors.feedback.alert300,
    titleKey: 'shift.resolutionStatus.expiringSoon.title',
    bodyKey: 'shift.resolutionStatus.expiringSoon.body',
  },
  valid: {
    icon: 'checkCircle',
    color: colors.important.main,
    titleKey: 'shift.resolutionStatus.valid.title',
    bodyKey: 'shift.resolutionStatus.valid.body',
  },
  none: {
    icon: 'minus',
    color: colors.grays.neutral99,
    titleKey: 'shift.resolutionStatus.none.title',
    bodyKey: 'shift.resolutionStatus.none.body',
  },
};

function resolveVariant({
  isExceptionalWorkingDay,
  dtResolutionExpired,
  dtResolutionExpiryDate,
}: ShiftResolutionStatusIndicatorProps): ResolutionStatusVariant {
  if (!isExceptionalWorkingDay || !dtResolutionExpiryDate) return 'none';
  if (dtResolutionExpired) return 'expired';
  const daysUntilExpiry = getDaysUntil(dtResolutionExpiryDate);
  if (daysUntilExpiry !== null && daysUntilExpiry <= DT_RESOLUTION_EXPIRY_WARNING_DAYS) return 'expiringSoon';
  return 'valid';
}

/**
 * Ícono + tooltip (título y texto) del estado de vigencia de la resolución DT en la
 * columna "Resolución" del listado de turnos (ASIS-5054). Generaliza el patrón de
 * `ShiftAssignedIndicator` (ancla + tooltip on hover/focus) a 4 variantes con ícono
 * real en vez de un punto de color.
 */
export const ShiftResolutionStatusIndicator: React.FC<ShiftResolutionStatusIndicatorProps> = ({
  isExceptionalWorkingDay,
  dtResolutionExpired,
  dtResolutionExpiryDate,
}) => {
  const { t: translate } = useTranslation('schedule');
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const variant = resolveVariant({
    isExceptionalWorkingDay,
    dtResolutionExpired,
    dtResolutionExpiryDate,
  });
  const config = VARIANT_CONFIG[variant];

  return (
    <span
      ref={anchorRef}
      className="inline-flex items-center justify-center"
      style={{ position: 'relative', cursor: 'help' }}
      tabIndex={0}
      aria-label={translate(config.titleKey)}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
      data-testid={`shift-resolution-status-indicator-${variant}`}
    >
      <IconScout name={config.icon} color={config.color} size={20} />
      {showTooltip && (
        <Tooltip
          title={translate(config.titleKey)}
          body={translate(config.bodyKey)}
          anchorRef={anchorRef}
          strategy="auto"
          placement="top"
          maxWidth={280}
        />
      )}
    </span>
  );
};

export default ShiftResolutionStatusIndicator;
